import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Library, Plus, Copy, Pencil, Trash2, Send, Search, Clock, X, Loader2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import MissionStudio from "@/components/MissionStudio";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";

const LANGS = ["", "es", "fr", "de", "it", "pt", "ja", "en"];
const LEVELS = ["", "A1", "A2", "B1", "B2", "C1", "C2"];

export default function MissionLibrary() {
  const { t, locale } = useI18n();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("");
  const [level, setLevel] = useState("");
  const [sort, setSort] = useState("recent");
  const [studio, setStudio] = useState(null); // {mission?, missionId?} or {} for new
  const [assignFor, setAssignFor] = useState(null); // mission being assigned
  const [classes, setClasses] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (lang) params.set("target_language", lang);
      if (level) params.set("level", level);
      params.set("sort", sort);
      const r = await api.get(`/missions?${params.toString()}`);
      setMissions(r.data.missions || []);
    } catch { /* not a teacher */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, lang, level, sort]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data.active || [])).catch(() => {}); }, []);

  const duplicate = async (id) => {
    try { await api.post(`/missions/${id}/duplicate`); toast.success(t("pm.library.duplicated")); load(); }
    catch { toast.error(t("pm.library.dupFailed")); }
  };
  const del = async (id) => {
    if (!window.confirm(t("pm.library.confirmDelete"))) return;
    try { await api.delete(`/missions/${id}`); toast.success(t("pm.library.deleted")); load(); }
    catch { toast.error(t("pm.library.delFailed")); }
  };

  return (
    <AppLayout>
      <Seo title="Mission Library · GetFluentLoop" noindex />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/teacher" className="text-sm text-gray-600 flex items-center gap-1"><ChevronLeft className="w-4 h-4 rtl:rotate-180" /> {t("teacher.dashboard")}</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
          <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2"><Library className="w-7 h-7" /> {t("pm.library.title")}</h1>
          <button onClick={() => setStudio({})} className="nb-btn-primary py-2 px-4 text-sm" data-testid="new-mission-btn"><Plus className="w-4 h-4" /> {t("pm.library.newMission")}</button>
        </div>
        <p className="text-gray-600 text-sm mt-1">{t("pm.library.intro")}</p>

        {/* filters */}
        <div className="nb-card p-3 mt-5 flex flex-wrap items-center gap-2" data-testid="mission-filters">
          <div className="flex items-center gap-2 flex-1 min-w-[180px]">
            <Search className="w-4 h-4 text-gray-400" />
            <input className="nb-input w-full border-0 shadow-none p-0 focus:ring-0" placeholder={t("pm.library.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="mission-search" />
          </div>
          <select className="nb-input py-1.5" value={lang} onChange={(e) => setLang(e.target.value)} data-testid="filter-language">{LANGS.map((v) => <option key={v} value={v}>{v ? langDisplayName(v, locale) : t("pm.library.allLanguages")}</option>)}</select>
          <select className="nb-input py-1.5" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="filter-level">{LEVELS.map((l) => <option key={l} value={l}>{l || t("pm.library.allLevels")}</option>)}</select>
          <select className="nb-input py-1.5" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort"><option value="recent">{t("pm.library.sortRecent")}</option><option value="last_used">{t("pm.library.sortLastUsed")}</option></select>
        </div>

        {loading ? (
          <div className="nb-card h-32 animate-pulse mt-5" />
        ) : missions.length === 0 ? (
          <div className="nb-card bg-butter p-6 mt-5 text-center" data-testid="library-empty">
            <p className="font-cabinet font-bold">{t("pm.library.empty")}</p>
            <p className="text-sm text-gray-700 mt-1">{t("pm.library.emptySub")}</p>
            <button onClick={() => setStudio({})} className="nb-btn-primary mt-4 inline-flex px-5 py-2" data-testid="library-empty-new-btn"><Plus className="w-4 h-4" /> {t("pm.library.newMission")}</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mt-5" data-testid="library-list">
            {missions.map((m) => (
              <div key={m.mission_id} className="nb-card p-4 flex flex-col" data-testid={`library-mission-${m.mission_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-cabinet font-bold truncate" dir="auto">{m.title}</div>
                    <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-0.5">
                      <span className="nb-tag bg-mint text-[10px] py-0">{m.target_language?.toUpperCase()}</span>
                      <span className="nb-tag bg-lavender text-[10px] py-0">{m.level}</span>
                      {m.estimated_minutes && <span>{t("pm.studio.minutes", { count: m.estimated_minutes })}</span>}
                    </div>
                  </div>
                  {m.status === "assigned" && <span className="nb-tag bg-sky text-[10px] py-0 shrink-0">{t("pm.library.used")}</span>}
                </div>
                <p className="text-xs text-gray-600 mt-2 line-clamp-2 flex-1" dir="auto">{m.learning_objective}</p>
                {m.last_used_at && <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-2"><Clock className="w-3 h-3" /> {t("pm.library.lastUsed", { date: new Date(m.last_used_at).toLocaleDateString(locale) })}</div>}
                <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                  <button onClick={() => setAssignFor(m)} className="nb-btn-primary py-1.5 px-3 text-xs" data-testid={`assign-${m.mission_id}`}><Send className="w-3 h-3 rtl:-scale-x-100" /> {t("pm.library.assign")}</button>
                  <button onClick={() => setStudio({ mission: m, missionId: m.mission_id })} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid={`edit-${m.mission_id}`}><Pencil className="w-3 h-3" /> {t("pm.library.edit")}</button>
                  <button onClick={() => duplicate(m.mission_id)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid={`duplicate-${m.mission_id}`} title={t("pm.library.duplicate")} aria-label={t("pm.library.duplicate")}><Copy className="w-3 h-3" /></button>
                  <button onClick={() => del(m.mission_id)} className="nb-btn-outline py-1.5 px-2 text-xs text-coral border-coral" data-testid={`delete-${m.mission_id}`} title={t("pm.library.delete")} aria-label={t("pm.library.delete")}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {studio && (
        <MissionStudio
          initialMission={studio.mission} missionId={studio.missionId}
          onClose={() => setStudio(null)} onSaved={() => { setStudio(null); load(); }} />
      )}
      {assignFor && (
        <AssignDialog mission={assignFor} classes={classes} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); load(); }} />
      )}
    </AppLayout>
  );
}

function AssignDialog({ mission, classes, onClose, onDone }) {
  const { t } = useI18n();
  const [sel, setSel] = useState([]);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const submit = async () => {
    if (!sel.length) { toast.error(t("pm.library.pickClass")); return; }
    setBusy(true);
    try {
      await api.post("/assignments", { mission_id: mission.mission_id, class_ids: sel, due_date: due || null });
      toast.success(t("pm.library.assigned", { count: sel.length }));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t("pm.library.assignFailed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-dialog">
      <div className="bg-paper border-2 border-ink rounded-2xl shadow-hard w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b-2 border-ink flex items-center justify-between">
          <h3 className="font-cabinet font-bold truncate">{t("pm.library.assignTitle", { title: mission.title })}</h3>
          <button onClick={onClose} data-testid="assign-dialog-close" aria-label={t("pm.library.close")} className="w-8 h-8 border-2 border-ink rounded-lg flex items-center justify-center bg-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs font-bold uppercase text-gray-500">{t("pm.library.chooseClasses")}</div>
          {classes.length === 0 && <p className="text-sm text-gray-500">{t("pm.library.noClasses")}</p>}
          <div className="space-y-2 max-h-56 overflow-auto no-scrollbar">
            {classes.map((c) => (
              <label key={c.class_id} className={`flex items-center gap-3 border-2 border-ink rounded-xl px-3 py-2 cursor-pointer ${sel.includes(c.class_id) ? "bg-mint/50" : "bg-white"}`} data-testid={`assign-class-${c.class_id}`}>
                <input type="checkbox" checked={sel.includes(c.class_id)} onChange={() => toggle(c.class_id)} />
                <span className="font-bold text-sm">{c.name}</span>
                <span className="text-xs text-gray-500 ms-auto">{c.target_language?.toUpperCase()} {c.level}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 mb-1">{t("pm.library.dueOptional")}</label>
            <input type="date" className="nb-input" value={due} onChange={(e) => setDue(e.target.value)} data-testid="assign-due-date" />
          </div>
          <button onClick={submit} disabled={busy} className="nb-btn-primary w-full py-3" data-testid="assign-submit">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 rtl:-scale-x-100" />} {t("pm.library.assignMission")}
          </button>
        </div>
      </div>
    </div>
  );
}
